import React from "react";
import {Property} from "csstype";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {usePrevious} from "../../../hooks/usePrevious";
import {useResizeDetector} from "../../../hooks/useResizeDetector";
import {BehaviorLines} from "../../../models/CellContent/BehaviorLines";
import {CellContent, EditorType} from "../../../models/CellContent";
import {BehaviorLine} from "./BehaviorLine";
import {JcEditorState} from "../../../models/CellContent/JcEditorState";

type BehaviorEditorProps = {
    isEdit: boolean;
    isSelected: boolean;
    cellWidth: number;
    lineBackgroundColor: Property.BackgroundColor;
    content: CellContent;
    readOnly: boolean;
    onBlur: (content: CellContent) => void;
    onChangeEditorHeight: (height: number) => void;
    onChangeContent: (content: CellContent) => void;
    editorType: EditorType;
}
export const BehaviorEditor = (props: BehaviorEditorProps) => {
    const [behavior_lines, makeContent] = props.content.useBehaviorLines();

    const setBehaviorLines = (behavior_lines: BehaviorLines) => {
        props.onChangeContent(makeContent(behavior_lines));
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );


    const previous_is_edit = usePrevious(props.isEdit);
    if (previous_is_edit === true && !props.isEdit) {
        props.onBlur(makeContent(behavior_lines));
    }

    const handleDragStart = (event: DragStartEvent) => {

    }
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            if (typeof active.id !== 'string' || typeof over.id !== 'string') {
                // UniqueIdentifier は string | number だが、SortableContext の items として渡しているのは string のみ
                throw new Error('unexpected sort id');
            }

            setBehaviorLines(behavior_lines.move(active.id, over.id));
        }
    }

    const handleAppend = () => {
        const new_data = behavior_lines.append();
        setBehaviorLines(new_data);
    }

    const handleRemove = (key: string) => {
        setBehaviorLines(behavior_lines.remove(key))
    }

    const handleChangeImage = (key: string, image: string) => {
        setBehaviorLines(behavior_lines.setImage(key, image))
    }
    const handleChangeEditorState = (key: string, editor_state: JcEditorState) => {
        setBehaviorLines(behavior_lines.setContent(key, editor_state))
    }


    const editor_ref = React.useRef<HTMLDivElement>(null);

    useResizeDetector(editor_ref, (_, height) => {
        props.onChangeEditorHeight(height);
    });

    return (
        <div className="behaviorEditor"
             ref={editor_ref}
             onKeyDown={(e) => e.stopPropagation()}  // ContentCell の onKeyDown を抑制
        >
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={behavior_lines.Keys} strategy={verticalListSortingStrategy}>
                    {behavior_lines.toArray().map((line) =>
                        <BehaviorLine key={line.key} sortKey={line.key}
                                      isEdit={props.isEdit} isSelected={props.isSelected}
                                      readOnly={props.readOnly}
                                      image={line.image}
                                      editorState={line.content}
                                      Remove={handleRemove}
                                      Append={handleAppend}
                                      ChangeImage={handleChangeImage}
                                      ChangeEditorState={handleChangeEditorState}
                                      editorType={props.editorType}
                                      maxWidth={props.cellWidth}
                                      lineBackgroundColor={props.lineBackgroundColor}
                        />
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
}
